import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

/**
 * MOPS-local dense operator table. Deliberately separate from
 * `@orn/shared-web/components/Table` — the shared-web primitive is styled for
 * SaaS dashboards (`h-14` rows, `rounded-2xl` card, `shadow-sm`) and cannot be
 * reconciled with the MOPS operator aesthetic documented in
 * `packages/mops-web/AGENTS.md` § Density + Visual chrome:
 *
 * - 28-32px row height
 * - zebra off, 1px borders over backgrounds
 * - sharp edges, no card chrome, no shadows
 * - `tabular-nums` for numeric alignment
 * - `font-mono` for IDs, tokens, emails, timestamps (opt-in via the `mono`
 *   prop on `Table.Cell`)
 * - sticky header (opt-in via the `sticky` prop on `Table.Header`)
 *
 * Uses native `<table>` / `<thead>` / `<tbody>` under the hood — no
 * react-aria-components. We do not need row selection, keyboard grid
 * navigation, or sort chrome for the admin surface; if we do later, extend
 * this component rather than reach for the shared-web primitive.
 */

interface ITableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
}

const TableRoot = ({ className, children, ...props }: ITableProps) => (
  <table className={cx('w-full border-collapse text-left text-sm tabular-nums', className)} {...props}>
    {children}
  </table>
);
TableRoot.displayName = 'Table';

interface ITableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  /** Pin the header to the top of its scroll container. */
  sticky?: boolean;
  children: React.ReactNode;
}

const TableHeader = ({ className, sticky = false, children, ...props }: ITableHeaderProps) => (
  <thead
    className={cx(
      'text-fg-secondary border-border-primary border-b text-xs font-medium uppercase tracking-wide',
      sticky && 'bg-bg-primary sticky top-0 z-10',
      className
    )}
    {...props}
  >
    {children}
  </thead>
);
TableHeader.displayName = 'Table.Header';

interface ITableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

const TableBody = ({ className, children, ...props }: ITableBodyProps) => (
  <tbody className={className} {...props}>
    {children}
  </tbody>
);
TableBody.displayName = 'Table.Body';

interface ITableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
}

const TableRow = ({ className, children, ...props }: ITableRowProps) => (
  <tr
    className={cx('border-border-primary hover:bg-bg-secondary border-b transition-colors last:border-b-0', className)}
    {...props}
  >
    {children}
  </tr>
);
TableRow.displayName = 'Table.Row';

interface ITableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
}

const TableHeaderCell = ({ className, children, ...props }: ITableHeaderCellProps) => (
  <th className={cx('px-3 py-2 font-medium', className)} {...props}>
    {children}
  </th>
);
TableHeaderCell.displayName = 'Table.HeaderCell';

interface ITableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Render contents in monospace — use for IDs, tokens, emails, timestamps, JSON. */
  mono?: boolean;
  /** Mute the text color to `text-fg-secondary` — use for metadata columns. */
  muted?: boolean;
  children?: React.ReactNode;
}

const TableCell = ({ className, mono = false, muted = false, children, ...props }: ITableCellProps) => (
  <td
    className={cx(
      'px-3 py-1.5 align-middle',
      mono && 'font-mono',
      muted ? 'text-fg-secondary' : 'text-fg-primary',
      className
    )}
    {...props}
  >
    {children}
  </td>
);
TableCell.displayName = 'Table.Cell';

const Table = TableRoot as typeof TableRoot & {
  Header: typeof TableHeader;
  Body: typeof TableBody;
  Row: typeof TableRow;
  HeaderCell: typeof TableHeaderCell;
  Cell: typeof TableCell;
};
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.HeaderCell = TableHeaderCell;
Table.Cell = TableCell;

export default Table;
